// src/pages/SentenceBuildingPage.tsx
// Enhanced click-to-build system optimized for motor planning accessibility
import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useLocation } from 'wouter'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { audio, storage } from '@/lib/utils'
import { SentenceDataLoader } from '@/utils/sentenceDataLoader'
import type { MoodState } from '@/types/sentenceTypes'
import { useAnalytics } from '../hooks/useAnalytics'
import { useCurrentUserId } from '@/lib/auth-utils'
import { useChildAuth } from '@/contexts/ChildAuthContext'
import { useSessionStore } from '../stores/sessionStore'

// Mood-specific UI configurations
const getMoodStyles = (mood: string) => {
  switch(mood) {
    case 'overwhelmed':
    case 'tired':
      return {
        containerBg: 'bg-[#FAF9F6]',
        cardBg: 'bg-white/60',
        spacing: 'space-y-10',
        slotSize: 'min-w-[120px] min-h-[72px] text-xl', // Bigger slots
        wordButtonSize: 'min-h-[60px] text-xl px-6 py-4', // Bigger word buttons
        gridCols: 'grid-cols-1', // One word per row - easier clicking
        hideProgress: true,
        shadow: '',
      }
    case 'energetic':
    case 'excited':
      return {
        containerBg: 'bg-gradient-to-b from-amber-100 to-sky-100',
        cardBg: 'bg-white',
        spacing: 'space-y-4',
        slotSize: 'min-w-[100px] min-h-[56px] text-base',
        wordButtonSize: 'min-h-[48px] text-base px-4 py-3',
        gridCols: 'grid-cols-2 lg:grid-cols-3',
        hideProgress: false,
        shadow: 'shadow-md',
      }
    default: // focused, steady
      return {
        containerBg: 'bg-[#FAF9F6]',
        cardBg: 'bg-white',
        spacing: 'space-y-6',
        slotSize: 'min-w-[100px] min-h-[56px] text-sm md:text-lg',
        wordButtonSize: 'min-h-[48px] text-base md:text-lg px-4 py-3',
        gridCols: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
        hideProgress: false,
        shadow: 'shadow-sm',
      }
  }
}

interface SentenceBuildingPageProps {
  theme: string
}

interface SentenceSlot {
  id: string
  type: 'fixed' | 'fillable'
  slotType?: string  // e.g., "actions", "creatures", "qualities"
  label?: string     // e.g., "What action?"
  content: string
  filled: boolean
  validWords?: string[]
}

type TtsAccent = 'US' | 'GB' | 'AU' | 'CA'

const getPatternCelebration = (template: SentenceTemplate): string => {
  // Use the teaching_point from the template as the celebration message
  return `✨ ${template.teaching_point}!`
}

const getPatternExample = (slots: SentenceSlot[]): string => {
  // Create a simple example following the same pattern
  const examples = {
    'WHO + DOING': 'Dogs run',
    'WHO + DESCRIBING': 'Cats are big', 
    'WHO + DOING + WHERE': 'Birds fly home',
    'DESCRIBING + WHO + DOING': 'Big dogs run',
    'WHO + DOING + COLOR': 'Dogs see red'
  }
  
  const pattern = slots
    .filter(slot => slot.type !== 'fixed' && slot.filled)
    .map(slot => getGrammarLabel(slot.type).split('(')[0].trim())
    .join(' + ')
    
  const example = examples[pattern] || 'The cat runs'
  return `This follows the same pattern as '${example}' - can you see it?`
}

// Simplified grammar helper - just returns the slot label or a generic fallback
const getGrammarLabel = (slotType: string): string => {
  // For new structure, slotType will be like "action", "description", etc.
  const genericLabels = {
    'action': 'action word',
    'description': 'describing word',
    'tech_thing': 'tech word',
    'mystery_thing': 'mystery word',
    'action_thing': 'action word',
    'scifi_thing': 'sci-fi word',
    'survival_thing': 'survival word',
    'when_where_how': 'context word'
  }
  return genericLabels[slotType] || `${slotType.replace('_', ' ')}`
}

const shouldShowSlot = (slots: SentenceSlot[], slotIndex: number): boolean => {
  // Always show fixed words
  if (slots[slotIndex].type === 'fixed') {
    return true
  }
  
  // Show if this slot is filled
  if (slots[slotIndex].filled) {
    return true
  }
  
  // Show if all previous fillable slots are filled
  for (let i = 0; i < slotIndex; i++) {
    if (slots[i].type !== 'fixed' && !slots[i].filled) {
      return false // Previous unfilled slot exists
    }
  }
  
  return true // All previous slots are filled/fixed
}

const getProgressiveMessage = (slots: SentenceSlot[], currentIndex: number): string => {
  const currentSlot = slots[currentIndex]
  if (!currentSlot || currentSlot.type === 'fixed') return ''
  
  const slotPosition = slots.filter((s, i) => i <= currentIndex && s.type !== 'fixed').length
  const totalSlots = slots.filter(s => s.type !== 'fixed').length
  
  if (slotPosition === 1) {
    return `First, choose ${getGrammarLabel(currentSlot.type)} to start your sentence...`
  } else if (slotPosition === totalSlots) {
    return `Finally, choose ${getGrammarLabel(currentSlot.type)} to complete your sentence!`
  } else {
    return `Great! Now choose ${getGrammarLabel(currentSlot.type)} to continue...`
  }
}

const getEducationalFeedback = (word: string, slot: SentenceSlot): string => {
  return `Great choice! "${word}" fits perfectly for: ${slot.label}`
}

const generateModelSentence = (template: SentenceTemplate): string => {
  let sentence = template.pattern
  
  // Replace each slot with first valid word (proper case)
  template.slots.forEach(slotDef => {
    if (slotDef.valid_words && slotDef.valid_words.length > 0) {
      const word = slotDef.valid_words[0]
      const properWord = word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      sentence = sentence.replace(`__${slotDef.id}__`, properWord)
    }
  })
  
  return sentence
}

const SentenceBuildingPage: React.FC<SentenceBuildingPageProps> = ({ theme }) => {
  const { currentBrainState } = useSessionStore()
  const [currentTemplateIndex, setCurrentTemplateIndex] = useState(0)
  const [sentenceSlots, setSentenceSlots] = useState<SentenceSlot[]>([])
  const [availableWords, setAvailableWords] = useState<string[]>([])
  const [currentSlotIndex, setCurrentSlotIndex] = useState<number | null>(null)
  const [isSentenceComplete, setIsSentenceComplete] = useState(false)
  const [showCelebration, setShowCelebration] = useState(false)
  const [isReading, setIsReading] = useState(false)
  const [wordFeedback, setWordFeedback] = useState<string>('')
  const [showHints, setShowHints] = useState(
    storage.get('current-brain-state') === 'overwhelmed' || 
    storage.get('current-brain-state') === 'tired'
  )
  const [currentMood, setCurrentMood] = useState<MoodState>('neutral')
  const [audioEnabled, setAudioEnabled] = useState(true) // TTS on by default
  const [lastClickTime, setLastClickTime] = useState<number>(0)
  const [lastClickedSlot, setLastClickedSlot] = useState<number>(-1)
  const [, setLocation] = useLocation()
  const userId = useCurrentUserId()
  const { childSession } = useChildAuth()
  const childId = childSession?.childId
  const { trackAnyActivity } = useAnalytics(childId || userId || undefined)
  const [showModelSentence, setShowModelSentence] = useState(true)

  // Get mood-responsive templates from new data structure
  const templates = SentenceDataLoader.getTemplatesForMood(theme, currentMood)
  const currentTemplate = templates[currentTemplateIndex]

  // Parse template pattern into slots
  const parseTemplateIntoSlots = (template: SentenceTemplate) => {
    const slots: SentenceSlot[] = []
    let remainingPattern = template.pattern
    
    // Process each slot definition
    template.slots.forEach((slotDef, index) => {
      // Find this slot marker in pattern
      const slotMarker = `__${slotDef.id}__`
      const parts = remainingPattern.split(slotMarker)
      
      // Add fixed text before this slot
      if (parts[0].trim()) {
        slots.push({
          id: `fixed-${index}-before`,
          type: 'fixed',
          content: parts[0].trim(),
          filled: true
        })
      }
      
      // Add fillable slot
      slots.push({
        id: slotDef.id,
        type: 'fillable',
        slotType: slotDef.type,
        label: slotDef.label,
        content: '',
        filled: false,
        validWords: slotDef.valid_words
      })
      
      // Continue with remaining pattern
      remainingPattern = parts[1] || ''
    })
    
    // Add any remaining fixed text
    const leftover = remainingPattern.trim();
    if (leftover && leftover !== ".") {
      slots.push({
        id: `fixed-end`,
        type: 'fixed',
        content: leftover,
        filled: true
      });
    }
    
    return slots
  }

  // Initialize sentence slots when template changes
  useEffect(() => {
    if (!currentTemplate) return
    
    // Parse template using new multi-slot structure
    const slots = parseTemplateIntoSlots(currentTemplate)
    setSentenceSlots(slots)
    
    // Collect ALL valid words from ALL slots
    const allWords = new Set<string>()
    currentTemplate.slots.forEach(slotDef => {
      slotDef.valid_words.forEach(word => allWords.add(word))
    })
    setAvailableWords(Array.from(allWords))
    
    // Auto-select first empty slot
    const firstEmptyIndex = slots.findIndex(slot => !slot.filled)
    setCurrentSlotIndex(firstEmptyIndex !== -1 ? firstEmptyIndex : null)
    
    setIsSentenceComplete(false)
    setShowCelebration(false)
    setWordFeedback('')
    setShowModelSentence(true)
  }, [currentTemplateIndex, theme, currentMood])

  // Check if sentence is complete
  useEffect(() => {
    const allSlotsFilled = sentenceSlots.every(slot => slot.filled)
    
    if (allSlotsFilled && sentenceSlots.length > 0) {
      setIsSentenceComplete(true)
      setShowCelebration(true)
      setCurrentSlotIndex(null)
      
      // Track sentence completion immediately
      if (childId || userId) {
        trackAnyActivity(
          'sentence_building',
          `Sentence: ${theme}`,
          0.5,
          { completed: true, skills: ['grammar', 'sentence_structure'] }
        ).catch(err => console.log('Analytics tracking failed:', err))
      }
      
      // Only read if audio is enabled
      if (audioEnabled && currentTemplate) {
        setTimeout(() => {
          const sentence = sentenceSlots.map(slot => slot.content).join(' ')
          handleReadAloud(sentence)
        }, 500)
      }
    } else {
      setIsSentenceComplete(false)
      setShowCelebration(false)
    }
  }, [sentenceSlots, audioEnabled])

  // Handle double-click/tap to remove words
  const handleFilledSlotClick = async (slotIndex: number) => {
    const now = Date.now()
    const timeDiff = now - lastClickTime
    
    // Double-click/tap detection (within 500ms)
    if (lastClickedSlot === slotIndex && timeDiff < 500) {
      // Double-click detected - remove the word
      await handleSlotClear(slotIndex)
    } else {
      // Single click - just give feedback
      setWordFeedback('💡 Double-click this word to remove it!')
      setTimeout(() => setWordFeedback(''), 2000)
    }
    
    setLastClickTime(now)
    setLastClickedSlot(slotIndex)
  }

  // Enhanced slot click handler with audio feedback
  const handleSlotClick = async (slotIndex: number) => {
    const slot = sentenceSlots[slotIndex]
    if (slot.type === 'fixed') return
    
    // If slot is filled, handle double-click for removal
    if (slot.filled) {
      await handleFilledSlotClick(slotIndex)
      return
    }
    
    setCurrentSlotIndex(slotIndex)
    setWordFeedback('')
    
    // Audio feedback for slot selection only if enabled
    if (audioEnabled && audio.isSpeechSynthesisSupported()) {
      const slotType = slot.type.replace('_', ' ')
      await handleReadAloud(`Choose a ${getGrammarLabel(slot.type)}`)
    }
  }

  // Enhanced word click handler with smart feedback
  const handleWordClick = async (word: string) => {
    if (currentSlotIndex === null) {
      // Auto-select first available slot if none selected
      const firstEmptySlot = sentenceSlots.findIndex(slot => !slot.filled && slot.type !== 'fixed')
      if (firstEmptySlot !== -1) {
        setCurrentSlotIndex(firstEmptySlot)
        // Retry with the auto-selected slot
        setTimeout(() => handleWordClick(word), 100)
        return
      }
      setWordFeedback('👆 First click on an empty box, then choose your word!')
      return
    }
    
    const currentSlot = sentenceSlots[currentSlotIndex]
    
    // Enhanced word validation with helpful feedback
    if (currentSlot.validWords && !currentSlot.validWords.includes(word)) {
      setWordFeedback(`💡 "${word}" doesn't fit here. Try a ${currentSlot.type} word!`)
      
      // Audio feedback for wrong choice only if enabled
      if (audioEnabled && audio.isSpeechSynthesisSupported()) {
        await handleReadAloud(`Try a ${currentSlot.type} word instead`)
      }
      
      // Clear feedback after 3 seconds
      setTimeout(() => setWordFeedback(''), 3000)
      return
    }
    
    // Success! Fill the slot
    const newSlots = [...sentenceSlots]
    newSlots[currentSlotIndex] = {
      ...currentSlot,
      content: word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
      filled: true
    }
    
    setSentenceSlots(newSlots)
    
    // Delay removal slightly so the next slot highlights before the bank clears
    setTimeout(() => {
      setAvailableWords(prev => prev.filter(w => w !== word))
    }, 15)
    
    // Educational feedback - only detailed on first time seeing this slot type
    const seenSlotTypes = storage.get('seen-slot-types', {} as Record<string, boolean>)
    const isFirstTime = !seenSlotTypes[currentSlot.type]

    // Check if this is the last fillable slot
    const remainingEmpty = newSlots.filter(s => s.type !== 'fixed' && !s.filled).length
    const isLastSlot = remainingEmpty === 0

    const educationalMessage = isLastSlot 
      ? `Perfect! 🎉`
      : isFirstTime 
        ? `Nice! ✨`
        : `Great! 🎯`

    setWordFeedback(educationalMessage)

    // Mark as seen if first time
    if (isFirstTime) {
      storage.set('seen-slot-types', { ...seenSlotTypes, [currentSlot.type]: true })
    }
    
    // Read the sentence so far (makes it feel alive!)
    if (audioEnabled && audio.isSpeechSynthesisSupported()) {
      // Only read up to the CURRENT slot (don't include future fixed words)
      const sentenceSoFar = newSlots
        .slice(0, currentSlotIndex + 1)  // Only slots up to and including current
        .filter(s => s.filled)
        .map(s => s.content)
        .join(' ')
      setTimeout(() => handleReadAloud(sentenceSoFar), 300)
    }
    
    // Auto-advance to next empty slot
    const nextEmptySlot = newSlots.findIndex((slot, index) => 
      index > currentSlotIndex && !slot.filled && slot.type !== 'fixed' && shouldShowSlot(newSlots, index)
    )
    
    const nextSlotIndex = nextEmptySlot === -1 ? null : nextEmptySlot
    
    // Clear feedback after showing success
    setTimeout(() => setWordFeedback(''), 1500)
    
    setCurrentSlotIndex(nextEmptySlot === -1 ? null : nextEmptySlot)
    
    // Clear feedback after 2 seconds
    setTimeout(() => setWordFeedback(''), 4000)
  }

  // Enhanced slot clearing with confirmation
  const handleSlotClear = async (slotIndex: number) => {
    const slot = sentenceSlots[slotIndex]
    if (slot.type === 'fixed' || !slot.filled) return
    
    // Clear the slot
    const newSlots = [...sentenceSlots]
    const originalWord = slot.content
    
    newSlots[slotIndex] = {
      ...slot,
      content: '',
      filled: false
    }
    
    setSentenceSlots(newSlots)
    
    // Add word back to available words
    setAvailableWords(prev => [...prev, originalWord].sort())
    
    // Select this slot for refilling
    setCurrentSlotIndex(slotIndex)
    
    // Feedback
    setWordFeedback(`🔄 "${originalWord}" is back in your word bank!`)
    
    // No audio feedback on removal
    
    setTimeout(() => setWordFeedback(''), 2000)
  }

  // FIXED: Get only relevant words for current slot type - LIMITED TO 3-4 WORDS
  const getFilteredWordsForDisplay = () => {
    // Don't show any words if no slot is selected
    if (currentSlotIndex === null) {
      return []
    }
    
    const currentSlot = sentenceSlots[currentSlotIndex]
    
    // Don't show words for fixed slots
    if (!currentSlot || currentSlot.type === 'fixed' || currentSlot.filled) {
      return []
    }
    
    // Only show words valid for THIS specific slot
    return availableWords.filter(word => 
      currentSlot.validWords && currentSlot.validWords.includes(word)
    )
  }

  const handleReadAloud = async (text: string) => {
    if (!audio.isSpeechSynthesisSupported()) return
    
    setIsReading(true)
    try {
      const savedAccent = storage.get('tts-accent', 'GB') as TtsAccent
      const selectedVoice = audio.getBestVoiceForAccent(savedAccent)

      await audio.speak(text, {
        voice: selectedVoice,
        rate: 0.8,
        pitch: 1.1,
        accent: savedAccent
      })
    } catch (error) {
      console.error('Text-to-speech error:', error)
    } finally {
      setIsReading(false)
    }
  }

  const handleNextTemplate = () => {
    if (currentTemplateIndex < templates.length - 1) {
      // More sentences to build
      setShowModelSentence(true)
      setCurrentTemplateIndex(currentTemplateIndex + 1)
    } else {
      // All sentences complete - go directly to story selection
      localStorage.setItem('sentence-building-theme', theme)
      setLocation('/interests')
    }
  }

  const handleReset = () => {
    // Parse template using new structure
    const slots = parseTemplateIntoSlots(currentTemplate)
    setSentenceSlots(slots)
    
    // Collect all valid words
    const allWords = new Set<string>()
    currentTemplate.slots.forEach(slotDef => {
      slotDef.valid_words.forEach(word => allWords.add(word))
    })
    setAvailableWords(Array.from(allWords))
    
    // Auto-select first empty slot
    const firstEmptyIndex = slots.findIndex(slot => !slot.filled)
    setCurrentSlotIndex(firstEmptyIndex !== -1 ? firstEmptyIndex : null)
    
    setShowModelSentence(true)
    setWordFeedback('')
  }

  const getThemeLabel = () => {
    switch (theme) {
      case 'tech': return 'Tech & Code'
      case 'mystery': return 'Mystery Solving'
      case 'action': return 'Action & Combat'
      case 'sci-fi': return 'Sci-Fi & Space'
      case 'survival': return 'Survival & Wilderness'
      default: return 'Sentence Building'
    }
  }

  const filteredWords = getFilteredWordsForDisplay()
  
  const moodStyles = getMoodStyles(currentBrainState?.id || 'focused')

  return (
    <div className={`min-h-screen ${moodStyles.containerBg}`}>
      <div className={`px-4 py-6 md:px-6 lg:px-8 max-w-6xl mx-auto ${moodStyles.spacing}`}>
        
        {/* Header - responsive */}
        <div className="text-center mb-2">
          <h1 className="text-lg font-medium text-[#2D3748]">
            Sentence Builder
          </h1>
        </div>

        {/* Model Sentence Display - Show before building */}
        {showModelSentence && (
          <Card className={`mb-3 ${moodStyles.cardBg} border border-[#E5E7EB] ${moodStyles.shadow}`}>
            <CardContent className="text-center py-2">
              <p className="text-sm font-medium text-[#2D3748] mb-1">
                Example:
              </p>
              <div className="text-xl md:text-2xl bg-white p-4 rounded-lg border border-blue-200 mb-4 font-bold text-blue-800">
                "{generateModelSentence(currentTemplate)}"
              </div>
              <p className="text-base md:text-lg text-blue-600 mb-6">
                Now you try making a sentence like this!
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => setShowModelSentence(false)}
                  className="min-h-[48px] bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 text-base font-medium"
                >
                  Let's Build! 🚀
                </Button>
                
                {audioEnabled && (
                  <Button
                    variant="outline"
                    onClick={() => handleReadAloud(generateModelSentence(currentTemplate))}
                    disabled={isReading}
                    className="min-h-[48px] text-base px-6 py-3 border-blue-300 text-blue-700 hover:bg-blue-50"
                  >
                    {isReading ? '🗣️ Reading...' : '🔊 Hear Example'}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}
        {!showModelSentence && (
          <>
          
            {/* Sentence Frame - responsive */}
            <Card className={`mb-6 ${moodStyles.cardBg} border-autism-secondary border-2 ${moodStyles.shadow}`}>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="text-center text-lg md:text-xl text-autism-primary flex-1">
                    ✍️ Your Sentence
                  </CardTitle>
                  
                  {/* Audio Toggle */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-autism-primary">Read aloud:</span>
                    <Button
                      variant={audioEnabled ? "default" : "outline"}
                      size="sm"
                      onClick={() => setAudioEnabled(!audioEnabled)}
                      className="text-xs px-3 py-1"
                    >
                      {audioEnabled ? 'ON' : 'OFF'}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-center text-[#2D3748] mb-1">
                  Tap a blank, then tap a word.
                </p>
                {/* Mobile-optimized sentence slots */}
                <div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-6">
                  {sentenceSlots.map((slot, index) => {
                    const isVisible = shouldShowSlot(sentenceSlots, index)
                    
                    return (
                      <div
                        key={slot.id}
                        onClick={() => handleSlotClick(index)}
                        className={`
                          relative ${moodStyles.slotSize} border rounded-lg
                          font-bold cursor-pointer transition-all duration-300 transform
                          ${!isVisible 
                            ? 'opacity-30 bg-gray-100 border-gray-200 cursor-not-allowed pointer-events-none' 
                            : slot.type === 'fixed' 
                              ? 'bg-transparent text-[#2D3748] text-xl font-normal border-none cursor-default' 
                              : currentSlotIndex === index && !slot.filled
                                ? 'bg-blue-50 border-[3px] border-[#2563EB] shadow-lg'
                                : slot.filled
                                  ? 'bg-white text-[#2D3748] border-l-4 border-[#10B981] border border-[#E5E7EB] hover:scale-105 shadow-sm'
                                  : 'bg-white border-2 border-dashed border-[#10B981] hover:bg-green-50 hover:border-[#059669]'
                          }
                        `}
                      >
                        <div className="text-center px-2">
                          {!isVisible ? (
                            <span className="text-xs text-gray-400">?</span>
                          ) : slot.filled ? (
                            slot.type === 'fixed' ? (
                              slot.content
                            ) : (
                              <div className="flex items-center gap-2 justify-center">
                                <span>{slot.content}</span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleSlotClear(index)
                                  }}
                                  className="px-2 py-1 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded text-sm font-bold"
                                  aria-label="Remove word"
                                >
                                  ✕
                                </button>
                              </div>
                            )
                          ) : slot.content ? (
                            slot.content
                          ) : (
                            <span className="text-xs md:text-sm text-[#059669] font-medium break-words">
                              {slot.label || getGrammarLabel(slot.type)}
                            </span>
                          )}
                        </div>
                        
                        {/* Selection indicator - only show if visible */}
                        {isVisible && currentSlotIndex === index && slot.type !== 'fixed' && (
                          <div className="absolute -top-2 -right-2 w-4 h-4 md:w-6 md:h-6 bg-autism-primary rounded-full flex items-center justify-center">
                            <span className="text-white text-xs md:text-sm font-bold">👆</span>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>

                {/* Progressive guidance message - PROMINENT */}
                {currentSlotIndex !== null && sentenceSlots[currentSlotIndex] && !sentenceSlots[currentSlotIndex].filled && (
                  <div className="text-center mb-4 p-4 bg-purple-100 border-2 border-purple-400 rounded-lg">
                    <p className="text-lg md:text-xl font-bold text-purple-800">
                      {getProgressiveMessage(sentenceSlots, currentSlotIndex)}
                    </p>
                  </div>
                )}

                {/* Word feedback - responsive */}
                {wordFeedback && (
                  <div className="text-center mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm md:text-base text-blue-700">{wordFeedback}</p>
                  </div>
                )}

              </CardContent>
            </Card>

            {/* Word Bank - mobile-optimized - Hide when sentence complete */}
            {!isSentenceComplete && (
              <Card className={`mb-2 ${moodStyles.cardBg} border border-[#E5E7EB] ${moodStyles.shadow}`}>
                <CardHeader>
                  <CardTitle className="text-center text-lg md:text-xl text-autism-primary">
                    {currentSlotIndex !== null && sentenceSlots[currentSlotIndex] 
                      ? `📝 ${sentenceSlots[currentSlotIndex].label || getGrammarLabel(sentenceSlots[currentSlotIndex].slotType || '')} Words`
                      : '📝 Word Bank'
                    }
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {!isSentenceComplete && (
                    <>
                      {currentSlotIndex === null ? (
                        <div className="text-center p-6 bg-purple-100 rounded-lg border-2 border-purple-300">
                          <p className="text-lg md:text-xl font-bold text-purple-800 mb-2">
                            Click on a blank space above to start!
                          </p>
                          <p className="text-sm md:text-base text-purple-600">
                            The words will appear here once you choose a spot to fill.
                          </p>
                        </div>
                      ) : filteredWords.length === 0 ? (
                        <div className="text-center p-6">
                          <div className="text-4xl md:text-6xl mb-4">🎯</div>
                          <p className="text-base md:text-lg text-autism-primary/70">
                            No more words available for this slot type!
                          </p>
                        </div>
                      ) : (
                        <div className={`grid ${moodStyles.gridCols} gap-3 md:gap-4`}>
                          {filteredWords.map(word => (
                            <Button
                              key={word}
                              onClick={() => handleWordClick(word)}
                              variant="outline"
                              size="comfortable"
                              className={`${moodStyles.wordButtonSize} border-2 border-[#E5E7EB] hover:border-[#10B981] hover:bg-green-50 hover:text-[#059669] transition-all duration-200 transform hover:scale-105`}
                            >
                              {word}
                            </Button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Celebration Banner */}
        {showCelebration && (
          <div className="mb-3 p-3 bg-[#D1FAE5] border-2 border-[#10B981] rounded-lg text-center shadow-sm">
            
            {/* micro success */}
            <p className="text-sm font-medium text-[#2D3748] mb-1">
              Great sentence!
            </p>

            {/* show completed sentence */}
            <p className="text-sm text-[#2D3748] mb-2">
              "{sentenceSlots.map(slot => slot.content).join(' ')}"
            </p>

            {/* subtle pattern teaching */}
            {currentTemplate && (
              <p className="text-xs text-[#2D3748] mb-2">
                {getPatternCelebration(currentTemplate)}
              </p>
            )}

            {/* keep read aloud button */}
            {audioEnabled && (
              <Button
                variant="outline"
                onClick={() => handleReadAloud(sentenceSlots.map(slot => slot.content).join(' '))}
                disabled={isReading}
                className="min-h-[32px] text-xs px-2 py-1"
              >
                {isReading ? 'Reading...' : 'Hear Again'}
              </Button>
            )}
          </div>
        )}

        {/* Controls - responsive */}
        <div className="flex flex-col sm:flex-row justify-center gap-3 mb-6">
          {/* Quiet utility button */}
          {!isSentenceComplete && (
            <Button
              variant="outline"
              onClick={handleReset}
              className="min-h-[40px] text-sm px-3 py-1 text-[#6B7280] border-[#E5E7EB]"
            >
              🔄 Start Over
            </Button>
          )}
          
          {/* Primary action - Next sentence */}
          {isSentenceComplete && currentTemplateIndex < templates.length - 1 && (
            <Button
              onClick={handleNextTemplate}
              className="min-h-[48px] bg-[#10B981] hover:bg-[#059669] text-white px-8 py-2 text-base font-medium shadow-sm"
            >
              Next →
            </Button>
          )}
          
          {/* Final choice - equal weight buttons */}
          {isSentenceComplete && currentTemplateIndex >= templates.length - 1 && (
            <>
              <Button
                variant="outline"
                onClick={() => setLocation('/practice-reading')}
                className="min-h-[48px] text-base px-6 py-2 border-2 border-[#E5E7EB]"
              >
                ← Back to Activities
              </Button>
              <Button
                onClick={handleNextTemplate}
                className="min-h-[48px] bg-[#10B981] hover:bg-[#059669] text-white px-6 py-2 text-base font-medium shadow-sm"
              >
                Stories →
              </Button>
            </>
          )}
        </div>

        {/* Progress Indicator - responsive */}
        <div className="text-center">
          <div className="flex justify-center gap-2 mb-2 flex-wrap">
            {templates.map((_, index) => (
              <div
                key={index}
                className={`w-3 h-3 md:w-4 md:h-4 rounded-full ${
                  index === currentTemplateIndex 
                    ? 'bg-autism-secondary' 
                    : index < currentTemplateIndex 
                      ? 'bg-green-400' 
                      : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
          <p className="text-xs md:text-sm text-autism-primary/70">
            Sentence {currentTemplateIndex + 1} of {templates.length}
          </p>
        </div>

        {/* Accessibility Information */}
        <div className="sr-only">
          <p>
            You are building sentences using a click-based system. First click on an empty slot to select it, 
            then click on a word from the word bank to fill that slot. Only words that fit the selected slot will 
            be shown. You can double-click filled words to remove them and try different options.
          </p>
        </div>
      </div>
    </div>
  )
}

export default SentenceBuildingPage